// src/tabs/logements/LivretPreviewPanel.jsx — Phase A.4 (ADR-114)
//
// Preview iframe externe pour l'éditeur admin livret.
//
// Q-D4=A acté : iframe externe (vs composant inline) :
//   - Évite de charger PublicLivretPage (1300+ LOC) dans bundle admin
//   - Preview = rendu strict voyageur final (fidélité)
//   - Isolation iframe : CSS voyageur ne pollue pas admin
//
// Toggle viewport desktop/mobile pour preview responsive.
// Bouton 'Ouvrir dans onglet' pour full-screen via window.open.
// Bouton 'Recharger preview' pour key bump (force re-mount iframe).
//
// B-2 (pessimist), révisé INFRA PR-C : CSP `frame-ancestors 'self'
// https://app.fullbnbzen.com` dans vercel.json (ex-X-Frame-Options SAMEORIGIN)
// autorise le rendu iframe depuis le même domaine ET depuis le domaine de
// gestion app.fullbnbzen.com (l'aperçu livret devient cross-origin quand
// l'admin est servi par app.* et le livret par sejour.*).
//
// Mode `inline` (correction L3) : embarque le MÊME rendu voyageur (iframe
// /livret/r/:token, gelé ADR-149) directement dans la colonne droite de
// l'éditeur — cadre « téléphone » = l'iframe réelle (plus d'enveloppe
// décorative). Le rendu voyageur lui-même n'est PAS modifié ; seul l'habillage
// admin change. Le bouton « Recharger » (reloadKey) reste disponible pour
// rafraîchir l'iframe après édition.

import { useState } from "react";
import { DS } from "../../lib/ui";
import { Eye, Monitor, Smartphone, RotateCcw, ExternalLink, X } from "lucide-react";

export default function LivretPreviewPanel({
  previewUrl,
  onClose,
  inline = false,
  onReload,
}) {
  const [viewport, setViewport] = useState("desktop");
  const [reloadKey, setReloadKey] = useState(0);

  // B6 — « Recharger » (inline + plein écran) : si le parent fournit onReload
  // (re-fetch du token-URL), on l'attend PUIS on bump la key. CONDITION DURE
  // (pessimist B4) : le key bump s'exécute TOUJOURS (finally) — jamais de
  // no-op silencieux si le re-fetch échoue, l'iframe est re-montée quand même.
  // Prop optionnelle → rétrocompatible (sans onReload : key bump seul).
  async function handleReload() {
    try {
      if (onReload) await onReload();
    } catch (e) {
      // Pas de catch muet (garde-fou CLAUDE.md) : on loggue, le finally
      // re-monte l'iframe quand même.
      console.error("LivretPreviewPanel — onReload a échoué :", e);
    } finally {
      setReloadKey((k) => k + 1);
    }
  }

  if (!previewUrl) return null;

  // ── Mode INLINE : cadre téléphone embarquant l'iframe voyageur réelle ──────
  if (inline) {
    return (
      <div
        style={{
          width: "100%",
          maxWidth: 360,
          margin: "0 auto",
          borderRadius: 26,
          background: DS.card,
          border: `1px solid ${DS.borderHover}`,
          boxShadow: "0 8px 28px rgba(20,51,80,0.12)",
          padding: 10,
          fontFamily: DS.fontBody,
        }}
      >
        {/* Mini-barre : label + recharger (l'iframe ne se rafraîchit pas seule
            après une édition → bump de reloadKey pour re-mount). */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 6px 10px" }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              color: DS.textSecondary,
            }}
          >
            <Eye size={12} strokeWidth={2} /> Aperçu voyageur
          </span>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={handleReload}
            title="Recharger l'aperçu (après modifications)"
            className="fbz-press"
            style={inlineReloadBtn}
          >
            <RotateCcw size={12} strokeWidth={1.8} /> Recharger
          </button>
        </div>
        <div
          style={{
            borderRadius: 18,
            overflow: "hidden",
            border: `0.5px solid ${DS.border}`,
            background: DS.white,
          }}
        >
          <iframe
            key={reloadKey}
            src={previewUrl}
            title="Aperçu livret voyageur"
            style={{
              width: "100%",
              maxWidth: "100%",
              height: 600,
              border: "none",
              background: DS.white,
              display: "block",
            }}
          />
        </div>
      </div>
    );
  }

  const isMobile = viewport === "mobile";
  const iframeWidth = isMobile ? 390 : "100%";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 999,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "stretch",
        padding: 24,
        fontFamily: DS.fontBody,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        style={{
          background: DS.white,
          borderRadius: 14,
          boxShadow: "0 8px 32px rgba(0,0,0,.3)",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: 1200,
          height: "100%",
          margin: "0 auto",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "12px 18px",
            borderBottom: `0.5px solid ${DS.border}`,
            background: DS.toolBg,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontFamily: DS.fontDisplay,
              fontSize: 15,
              fontWeight: 600,
              color: DS.marineDark,
            }}
          >
            <span style={{
              display: "inline-flex", width: 26, height: 26, borderRadius: 7,
              background: DS.cielSoft, color: DS.marine, alignItems: "center", justifyContent: "center",
            }}>
              <Eye size={15} strokeWidth={1.8} />
            </span>
            Aperçu voyageur
          </div>

          {/* Toggle viewport desktop / mobile */}
          <div
            style={{
              display: "flex",
              gap: 4,
              background: DS.card,
              borderRadius: 8,
              padding: 4,
              border: `0.5px solid ${DS.border}`,
            }}
          >
            <button
              type="button"
              onClick={() => setViewport("desktop")}
              style={vpBtn(viewport === "desktop")}
              aria-label="Aperçu desktop"
            >
              <Monitor size={13} strokeWidth={1.8} /> Desktop
            </button>
            <button
              type="button"
              onClick={() => setViewport("mobile")}
              style={vpBtn(viewport === "mobile")}
              aria-label="Aperçu mobile"
            >
              <Smartphone size={13} strokeWidth={1.8} /> Mobile
            </button>
          </div>

          <div style={{ flex: 1 }} />

          <button
            type="button"
            onClick={handleReload}
            title="Recharger l'aperçu (après modifications)"
            className="fbz-press"
            style={chromeBtn}
          >
            <RotateCcw size={13} strokeWidth={1.8} /> Recharger
          </button>
          <button
            type="button"
            onClick={() => window.open(previewUrl, "_blank", "noopener")}
            title="Ouvrir dans un nouvel onglet"
            className="fbz-press"
            style={chromeBtn}
          >
            <ExternalLink size={13} strokeWidth={1.8} /> Ouvrir
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer l'aperçu"
            className="fbz-press"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              background: DS.marine,
              color: "#fff",
              border: "none",
              borderRadius: DS.radiusControl,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: DS.fontBody,
            }}
          >
            <X size={14} strokeWidth={2} /> Fermer
          </button>
        </div>

        <div
          style={{
            flex: 1,
            overflow: "auto",
            background: "#e2e8f0",
            padding: isMobile ? 24 : 0,
            display: "flex",
            justifyContent: "center",
            alignItems: isMobile ? "flex-start" : "stretch",
          }}
        >
          <iframe
            key={reloadKey}
            src={previewUrl}
            title="Aperçu livret voyageur"
            style={{
              width: iframeWidth,
              maxWidth: "100%",
              height: "100%",
              minHeight: 600,
              border: isMobile ? `1px solid ${DS.border}` : "none",
              borderRadius: isMobile ? 12 : 0,
              background: DS.white,
              boxShadow: isMobile ? "0 4px 14px rgba(0,0,0,0.12)" : "none",
            }}
          />
        </div>
      </div>
    </div>
  );
}

const vpBtn = (active) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "5px 12px",
  background: active ? DS.marine : "transparent",
  color: active ? "#fff" : DS.textSecondary,
  border: "none",
  borderRadius: 5,
  fontSize: 11,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: DS.fontBody,
});

const chromeBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 12px",
  background: DS.card,
  border: `0.5px solid ${DS.borderHover}`,
  borderRadius: DS.radiusControl,
  color: DS.marine,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: DS.fontBody,
};

// Bouton « Recharger » du mode inline (plus discret que le chrome plein écran).
const inlineReloadBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "4px 10px",
  background: DS.card,
  border: `0.5px solid ${DS.borderHover}`,
  borderRadius: DS.radiusControl,
  color: DS.marine,
  fontSize: 11,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: DS.fontBody,
};
